import { Grid, Typography, debounce, Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { setData, setServiceFunction } from "./../../../../store/formSlice";
import FormTextField from "./../../../../Controllers/Input/index";
import service from "./../../helpers/GifService";
import {
  debounceDuration,
  safeValue,
} from "./../../siteMap/navigations/helpers";
import ApplicantSelector from "../../Components/applicantSelector";
import {
  canEditSelector,
  reportingAsSelector,
} from "../../../../store/applicantsSlice";
import { useSelector } from "react-redux";
import { useLoading } from "../../../../providers/loadingProvider";

const pageName = "Parallel funding";

const fields = {
  agencies: "agencies",
  otherSources: "otherSources",
  eudip: "eU_DIP",
  otherSourcesPreviously: "otherSourcesPreviously",
  continuationCooperative: "continuationCooperative",
  previouseApplications: "previouseApplications",
  patents: "patents",
};

const ParallelFunding = (props) => {
  const methods = useForm();
  const dispatch = useDispatch();
  const { setValue, getValues, formState, watch } = methods;
  const [funding, setFunding] = useState();
  const [applicant, setApplicant] = useState();
  const loading = useLoading();
  const watchAll = watch();
  const as = useSelector(reportingAsSelector);
  const canEdit = useSelector(canEditSelector);

  const watchersEntries = Object.keys(fields).map((k) => [
    fields[k],
    watch(`is_${fields[k]}`),
  ]);
  const watchers = Object.fromEntries(watchersEntries);

  /*
    this is creating watchres for the bit fields.
    {
      agencies: true
      continuationCooperative: true
      eu_dip: true
      otherSources: true
      otherSourcesPreviously: true
      patents: true
      previouseApplications: true
    }
  */

  useEffect(() => {
    dispatch(setServiceFunction(`updateFunding`));
  }, [dispatch]);

  // set form state in the store
  const debouncedSetData = useMemo(
    () =>
      debounce(() => {
        if (!funding) return;
        const dirty = Object.keys(formState.dirtyFields);
        const values = getValues(dirty);
        const fieldsToEmpty = Object.keys(watchers).filter(
          (key) => watchers[key] === false
        );
        if (dirty.length === 0 && fieldsToEmpty.length === 0) return;
        fieldsToEmpty.forEach((f) => {
          values[f] = "";
        });
        const fundingToUpdate = {
          entityid: funding.entityID,
          ...values,
        };

        dispatch(setData({ funding: fundingToUpdate }));
      }, debounceDuration),
    [dispatch, formState.dirtyFields, funding, getValues, watchers]
  );

  useEffect(() => {
    debouncedSetData();
  }, [debouncedSetData, watchAll]);

  useEffect(() => {
    if (!Boolean(funding)) return;
    const {
      agencies,
      otherSources,
      eU_DIP,
      continuationCooperative,
      previouseApplications,
      otherSourcesPreviously,
      patents,
    } = funding;

    setValue(fields.agencies, safeValue(agencies));
    setValue(fields.otherSources, safeValue(otherSources));
    setValue(fields.eudip, safeValue(eU_DIP));
    setValue(
      fields.continuationCooperative,
      safeValue(continuationCooperative)
    );
    setValue(fields.previouseApplications, safeValue(previouseApplications));
    setValue(fields.otherSourcesPreviously, safeValue(otherSourcesPreviously));
    setValue(fields.patents, safeValue(patents));

    // look for the related bit fields and if there is a value in the textbox set them to true
    Object.keys(fields).forEach((key) => {
      setValue(`is_${fields[key]}`, Boolean(funding[fields[key]]));
    });
  }, [setValue, funding]);

  useEffect(() => {
    const getFunding = async () => {
      const loadingId = loading.addLoading("loading funding");

      const funding = await service.getFunding({
        fundingId: applicant.parallelFunding,
      });
      setFunding(funding);
      loading.removeLoading(loadingId);
    };
    if (applicant && applicant.parallelFunding) getFunding();
  }, [applicant, loading]);

  useEffect(() => {
    const getApplicant = async () => {
      const loadingId = loading.addLoading("loading applicant data");
      const applicant = await service.getApplicant();
      setApplicant(applicant);
      loading.removeLoading(loadingId);
    };
    getApplicant();
  }, [as, loading]);
  const Question = (label, name) => {
    return (
      <Box mb={3}>
        <Grid item xs={12}></Grid>
        <Grid item xs={12}>
          <Typography style={{ whiteSpace: "pre-line" }}>{label}</Typography>
          <FormTextField multiline name={name} disabled={!canEdit} />
        </Grid>
      </Box>
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Grid container spacing={2}>
          <Grid item>
            <Typography variant="subtitle2">{pageName}</Typography>
          </Grid>
          <Grid item>
            <InfoOutlinedIcon color="primary" />
          </Grid>
          <ApplicantSelector />
          <Grid item>
            {Question(
              "Is this, or related research presently being supported by other sources? if so, indicate other sources, amounts and dates of performing the research under this support",
              fields.otherSources
            )}
            {Question(
              "Has this research been submitted to other agencies? if so, indicate which agency, when and with what results.",
              fields.agencies
            )}
            {Question(
              "Has this research been submitted part or as a whole, within the framework of the EU and/or DIP program? If yes, please list the names of all counterparts involved and the application and grant no.",
              fields.eudip
            )}
            {Question(
              "If the research previously received support from other sources, list rights and obligations of grantor still in effect",
              fields.otherSourcesPreviously
            )}
            {Question(
              "If this is a continuation of any German-Israeli cooperative research, please indicate the supporting agency, duration and budget.",
              fields.continuationCooperative
            )}
            {Question(
              "List all previous applications to GIF by numbers:",
              fields.previouseApplications
            )}
            {Question(
              "To be filled in for Collaborative Proposal ONLY:\r\nDo you have any patent application or a registered patent relevant to this proposal? If yes, is your German/Israeli counterpart familiar with it? Please ask him to confirm with his signature here. Please give full information on pre-existing patentable inventions, intellectual and other proprietary rights (if any) related to the present proposal on a separate page.",
              fields.patents
            )}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default ParallelFunding;
